import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import { useArtistAuth } from '@/context/ArtistAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const statusLabels = {
  no_response: 'Awaiting your reply',
  interested: 'Interested',
  passed: 'Passed',
};

const statusClasses = {
  no_response: 'bg-amber-50 text-amber-700 border-amber-200',
  interested: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  passed: 'bg-slate-100 text-slate-700 border-slate-200',
};

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (error) {
    return value;
  }
};

const ArtistMatches = () => {
  const { artist } = useArtistAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [message, setMessage] = useState('');
  const [showPendingOnly, setShowPendingOnly] = useState(false);

  const loadMatches = async () => {
    if (!artist?.id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/casting/artist/${artist.id}/matches`);
      setMatches(response.data?.matches || []);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to load your matches right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artist?.id]);

  const respond = async (matchId, action) => {
    setBusyId(`${matchId}:${action}`);
    setMessage('');
    try {
      await axios.patch(`${API}/casting/match/${matchId}/respond`, { action });
      setMatches((current) =>
        current.map((item) => (item.id === matchId ? { ...item, artist_status: action } : item))
      );
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to save your response.');
    } finally {
      setBusyId('');
    }
  };

  const visibleMatches = useMemo(() => {
    if (!showPendingOnly) return matches;
    return matches.filter((item) => item.artist_status === 'no_response');
  }, [matches, showPendingOnly]);

  return (
    <main className="bg-slate-50 px-4 py-10 md:px-8">
      <SEO
        title="Artist Match History"
        description="See which client briefs Kalamanch AI matched to your artist profile."
        keywords="KalaManch artist matches, artist opportunity history, AI casting alerts"
      />
      <section className="mx-auto max-w-5xl rounded-[28px] border border-orange-100 bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-orange-500">Artist Matches</p>
        <h1 className="mt-4 text-4xl font-black text-slate-950">Opportunities matched to your profile</h1>
        <p className="mt-3 max-w-3xl text-lg leading-8 text-slate-600">
          KalaManch AI brings relevant briefs here so you can respond fast when a client requirement matches your profile.
        </p>
        {message ? <div className="mt-5 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">{message}</div> : null}
        {loading ? <div className="mt-8 rounded-2xl bg-slate-50 px-6 py-10 text-center text-slate-600">Loading your match history...</div> : null}
        {!loading && matches.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-200 px-6 py-12 text-center text-slate-600">
            No casting matches yet. As soon as a client brief fits your profile, it will appear here.
            <div className="mt-6">
              <Link to="/ai-manager" className="inline-flex rounded-full border border-orange-300 px-5 py-3 text-sm font-semibold text-orange-600 transition hover:bg-orange-50">
                Open AI Manager
              </Link>
            </div>
          </div>
        ) : null}
        {!loading && matches.length > 0 ? (
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-orange-50 px-4 py-2 text-sm font-bold text-orange-700">
              {matches.length} total match{matches.length === 1 ? '' : 'es'}
            </span>
            <span className="rounded-full bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700">
              {matches.filter((item) => item.artist_status === 'no_response').length} awaiting reply
            </span>
            <button
              type="button"
              onClick={() => setShowPendingOnly((current) => !current)}
              className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                showPendingOnly
                  ? 'bg-violet-700 text-white'
                  : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {showPendingOnly ? 'Showing awaiting only' : 'Show awaiting only'}
            </button>
          </div>
        ) : null}
        {!loading && matches.length > 0 ? (
          <div className="mt-8 space-y-5">
            {visibleMatches.map((match) => (
              <article key={match.id} className="rounded-[24px] border border-slate-200 bg-slate-50 p-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-violet-700 shadow-sm">
                      Match score {match.match_score}
                    </div>
                    <h2 className="mt-3 text-2xl font-black text-slate-950">
                      {match.brief?.parsed?.occasion_type || 'Creative opportunity'} in {match.brief?.parsed?.location || 'India'}
                    </h2>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700">
                        {match.brief?.parsed?.art_form || 'Artist brief'}
                      </span>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700">
                        Budget {match.brief?.parsed?.budget_range || match.brief?.parsed?.budget_inr || 'Not shared'}
                      </span>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700">
                        {formatDate(match.brief?.parsed?.event_date || match.brief?.event_date)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-7 text-slate-600">{match.brief?.raw_text}</p>
                    {match.brief?.client_name ? (
                      <p className="mt-2 text-sm font-semibold text-orange-700">Posted by {match.brief.client_name}</p>
                    ) : null}
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Matched on {formatDate(match.created_at)}
                    </p>
                  </div>
                  <div className={`rounded-full border px-4 py-2 text-sm font-semibold shadow-sm ${statusClasses[match.artist_status] || statusClasses.no_response}`}>
                    {statusLabels[match.artist_status] || 'Awaiting reply'}
                  </div>
                </div>
                <div className="mt-4 rounded-[20px] bg-white px-4 py-4 text-sm leading-7 text-slate-700">
                  <p className="font-semibold text-slate-900">Why you were matched</p>
                  <p className="mt-2">{match.match_reason}</p>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => respond(match.id, 'interested')}
                    disabled={busyId === `${match.id}:interested`}
                    className="rounded-full bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:opacity-60"
                  >
                    {busyId === `${match.id}:interested` ? 'Saving...' : 'Interested'}
                  </button>
                  <button
                    type="button"
                    onClick={() => respond(match.id, 'passed')}
                    disabled={busyId === `${match.id}:passed`}
                    className="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
                  >
                    {busyId === `${match.id}:passed` ? 'Saving...' : 'Pass'}
                  </button>
                  {match.artist_status !== 'no_response' ? (
                    <button
                      type="button"
                      onClick={() => respond(match.id, 'no_response')}
                      disabled={busyId === `${match.id}:no_response`}
                      className="rounded-full border border-violet-200 px-5 py-3 text-sm font-semibold text-violet-700 transition hover:bg-violet-50 disabled:opacity-60"
                    >
                      {busyId === `${match.id}:no_response` ? 'Saving...' : 'Reset response'}
                    </button>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        ) : null}
        {!loading && matches.length > 0 && visibleMatches.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-200 px-6 py-10 text-center text-slate-600">
            No pending replies right now. Switch the filter off to see your full match history.
          </div>
        ) : null}
      </section>
    </main>
  );
};

export default ArtistMatches;
